import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, Send, FileText, AlertTriangle, CheckCircle } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { submitNewsArticle, ArticleSubmission, getVolunteerProfile } from '@/lib/api';

interface FormData extends Omit<ArticleSubmission, 'featured_image'> {
  featured_image: File | null;
}

const VolunteerNewsSubmit: React.FC = () => {
  const navigate = useNavigate();
  const [volunteerName, setVolunteerName] = useState('');
  const [volunteerEmail, setVolunteerEmail] = useState('');
  const [formData, setFormData] = useState<FormData>({
    title: '',
    excerpt: '',
    content: '',
    author_name: '',
    author_email: '',
    author_phone: '',
    featured_image: null,
  });

  // Load volunteer data on mount
  useEffect(() => {
    const token = localStorage.getItem('volunteer_token');
    const name = localStorage.getItem('volunteer_name');

    if (!token) {
      navigate('/volunteer/login');
      return;
    }

    // Set author data from logged in user
    setFormData(prev => ({
      ...prev,
      author_name: name || 'Relawan',
      author_email: '', // Will be set from API response
      author_phone: '', // Will be set from API response
    }));
    setVolunteerName(name || 'Relawan');

    // Get volunteer profile to get email and phone
    const getProfile = async () => {
      try {
        const profile = await getVolunteerProfile(token);
        setVolunteerEmail(profile.email || '');
        setFormData(prev => ({
          ...prev,
          author_email: profile.email || '',
          author_phone: profile.phone || '',
        }));
      } catch (error) {
        console.error('Failed to get volunteer profile:', error);
      }
    };

    getProfile();
  }, [navigate]);

  const [imagePreview, setImagePreview] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const handleInputChange = (field: keyof FormData, value: string | File | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, featured_image: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleContentChange = (content: string) => {
    handleInputChange('content', content);

    // Auto-generate excerpt from first 150 characters of content
    if (!formData.excerpt.trim() && content.trim()) {
      const plainText = content.replace(/<[^>]*>/g, ''); // Remove HTML tags
      const autoExcerpt = plainText.substring(0, 150).trim();
      if (autoExcerpt) {
        handleInputChange('excerpt', autoExcerpt + (plainText.length > 150 ? '...' : ''));
      }
    }
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!formData.title.trim()) {
      errors.push('Judul artikel wajib diisi');
    }

    if (formData.title.length < 10) {
      errors.push('Judul minimal 10 karakter');
    }

    if (!formData.excerpt.trim()) {
      errors.push('Ringkasan artikel wajib diisi');
    }

    if (formData.excerpt.length < 20) {
      errors.push('Ringkasan minimal 20 karakter');
    }

    if (!formData.content.trim() || formData.content === '<p><br></p>') {
      errors.push('Konten artikel wajib diisi');
    }

    if (formData.content.length < 100) {
      errors.push('Konten artikel minimal 100 karakter');
    }

    if (!formData.author_name.trim()) {
      errors.push('Nama penulis wajib diisi');
    }

    if (!formData.author_email.trim()) {
      errors.push('Email penulis wajib diisi');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.author_email)) {
      errors.push('Format email tidak valid');
    }

    if (!formData.author_phone.trim()) {
      errors.push('Nomor telepon wajib diisi');
    }

    if (formData.featured_image) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(formData.featured_image.type)) {
        errors.push('Format gambar harus JPEG, PNG, atau GIF');
      }

      if (formData.featured_image.size > 2 * 1024 * 1024) {
        errors.push('Ukuran gambar maksimal 2MB');
      }
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrorMessage(validationErrors.join('. '));
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const submissionData = {
        ...formData,
        content: formData.content,
        type: 'volunteer_submission',
      };

      await submitNewsArticle(submissionData);
      setSubmitStatus('success');

      // Reset form after successful submission (but keep author info)
      setFormData(prev => ({
        ...prev,
        title: '',
        excerpt: '',
        content: '',
        featured_image: null,
      }));
      setImagePreview('');

    } catch (error: any) {
      console.error('Error submitting article:', error);
      setErrorMessage(error.response?.data?.message || 'Terjadi kesalahan saat mengirim artikel. Silakan coba lagi.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/volunteer/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kembali
          </Button>

          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Kirim Artikel Berita
            </h1>
            <p className="text-gray-600">
              Bagikan informasi bermanfaat untuk komunitas MindfulMe. Artikel akan ditinjau oleh admin sebelum dipublikasikan.
            </p>
          </div>
        </div>

        {submitStatus === 'success' && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>Artikel berhasil dikirim!</strong> Artikel Anda sedang dalam proses tinjauan oleh admin.
              Anda akan menerima pemberitahuan melalui email setelah artikel ditinjau.
            </AlertDescription>
          </Alert>
        )}

        {submitStatus === 'error' && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {errorMessage}
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Form Pengiriman Artikel
            </CardTitle>
            <CardDescription>
              Isi form di bawah ini untuk mengirim artikel berita Anda.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informasi Penulis - Auto-filled */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">Informasi Penulis</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Anda login sebagai:</p>
                  <p className="font-medium">{volunteerName}</p>
                  {volunteerEmail && <p className="text-sm text-gray-500">{volunteerEmail}</p>}
                </div>
              </div>

              {/* Informasi Artikel */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Informasi Artikel</h3>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Judul Artikel *</Label>
                    <Input
                      id="title"
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Judul yang menarik dan informatif"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Minimal 10 karakter
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="excerpt">Ringkasan *</Label>
                    <Textarea
                      id="excerpt"
                      value={formData.excerpt}
                      onChange={(e) => handleInputChange('excerpt', e.target.value)}
                      placeholder="Ringkasan singkat tentang artikel Anda"
                      rows={3}
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Minimal 20 karakter
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="featured_image">Gambar Utama</Label>
                    <div className="space-y-2">
                      <Input
                        id="featured_image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="file:mr-4 file:py-2 file:px-4"
                      />

                      {imagePreview && (
                        <div className="mt-2">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="h-48 w-auto object-cover rounded-lg border"
                          />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      Format: JPEG, PNG, GIF. Maksimal 2MB
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="content">Konten Artikel *</Label>
                    <div className="border rounded-lg">
                      <ReactQuill
                        value={formData.content}
                        onChange={handleContentChange}
                        modules={quillModules}
                        theme="snow"
                        placeholder="Tulis artikel Anda di sini..."
                        style={{ minHeight: '300px' }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Minimal 100 karakter
                    </p>
                  </div>
                </div>
              </div>

              {/* Informasi Proses */}
              <div className="border-t pt-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Proses Tinjauan</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Artikel akan ditinjau oleh tim admin MindfulMe</li>
                    <li>• Proses tinjauan biasanya memakan waktu 1-3 hari kerja</li>
                    <li>• Anda akan menerima pemberitahuan melalui email</li>
                    <li>• Artikel yang disetujui akan dipublikasikan di halaman berita</li>
                  </ul>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  disabled={isSubmitting}
                >
                  Batal
                </Button>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Mengirim...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Kirim Artikel
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VolunteerNewsSubmit;